/////////////////////////////////////////////////////////////////
// src/pages/ProductDetail.jsx — v15
// CHANGED FROM v14 (31 Jul 2026): REAL FIX — reordered the pd-id-row so
// the copy button sits directly after the Product ID value, with the
// date pushed after the copy button (was ID → date → copy, now
// ID → copy → date), per Amit's correction.
// CHANGED FROM v13 (31 Jul 2026): added the post date next to Product
// ID in the pd-id-row box, separated by a "|" divider, same formatDate
// pattern as ProductCard.jsx v22. Uses product.updateDate. Only shown
// when updateDate exists.
// CHANGED FROM v12 (20 Jul 2026): resellers can now CHOOSE which up
// to 5 images to share, instead of always getting the same first 5 in
// stored order — a thumbnail picker was added to the top of the
// Share & Earn popup. Defaults to the first 5 pre-selected (same
// behavior as before if nobody touches it), tap any thumbnail to
// toggle it on/off, capped at 5 selected at once. handleShare() now
// uses selectedImageIndices instead of always slicing images[0..5].
// CHANGED FROM v11 (20 Jul 2026): Share & Earn now attaches up to 5
// images instead of 2 (Amit's call, well within Chrome's Web Share
// API limit of 10 files/50MB).
// CHANGED FROM v10 (19 Jul 2026): fixed the Share & Earn image bug —
// product images are hosted on Google Drive, which doesn't send CORS
// headers, so the browser's direct fetch(driveUrl) call in handleShare()
// was ALWAYS silently failing (caught by an empty catch block) and
// falling back to text-only sharing, every time, on every device. Now
// routes each image through the new /.netlify/functions/image-proxy
// (fetches server-side, no CORS restriction there, streams the bytes
// back from our own domain instead). Also uses the image's REAL
// content-type instead of hardcoding image/jpeg, since a mismatched
// type can make navigator.canShare() silently reject the file on some
// devices. Errors are now logged to the console instead of silently
// swallowed, so any future failure is actually visible for debugging.
// CHANGED FROM v8: added "edit mode" — when arriving here via Cart's
// "tap to fix" flow (which passes editKey/editSize/editColorCode/editQty
// in navigation state), the page now pre-fills whatever selection was
// already made, and completing the missing field + tapping the CTA
// updates that SAME cart line via the new updateCartItemSelection()
// (StoreContext v10) instead of creating a duplicate/new line. This is
// the real fix for the "Color or size missing" checkout loop — previously
// there was no way to resume an incomplete cart line, only start fresh.
// Also defensively handles addToCart's new { error } return (StoreContext
// v10 now validates size/color itself) even though the on-screen
// canAddToCart check should already prevent this in normal use.
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useProducts } from '../hooks/useProducts';
import { useStore } from '../context/StoreContext';
import { pixelViewContent, pixelAddToCart } from '../utils/pixel';
const GARMENT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const SHOE_SIZES = ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDate(date) {
  if (!date || isNaN(date.getTime())) return '';
  const day = date.getDate();
  const month = MONTH_ABBR[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  return `${day} ${month} '${year}`;
}
const SHARE_TRANSLATIONS = {
  en: { lang: 'English', title: '🎉 Awesome! Ready to hustle with us?', note1: '✅ We will NEVER share our website, WhatsApp number, prices, or any details that connect back to us. Your business stays yours.', note2: '✅ Add your own price and keep your margin — that\'s your call entirely.', whatTitle: 'What gets shared:', what1: '📝 Product name & description', what2: '🖼 2 product images (screenshot the page for more, crop as needed)', what3: '🔑 Product ID (save this — you\'ll need it to place the order)', how: '💪 Once your customer confirms, place the order using the Product ID. Share your delivery address and complete payment — we handle the rest. Happy selling!', btn: 'Share Now', cancel: 'Cancel' },
  hi: { lang: 'हिंदी', title: '🎉 शानदार! हमारे साथ कमाई के लिए तैयार हैं?', note1: '✅ हम कभी भी अपनी वेबसाइट, व्हाट्सएप नंबर, कीमत या कोई भी जानकारी शेयर नहीं करेंगे।', note2: '✅ अपनी कीमत खुद तय करें और मुनाफा रखें।', whatTitle: 'क्या शेयर होगा:', what1: '📝 प्रोडक्ट का नाम और विवरण', what2: '🖼 2 प्रोडक्ट इमेज (और इमेज के लिए स्क्रीनशॉट लें)', what3: '🔑 प्रोडक्ट ID (ऑर्डर के लिए जरूरी)', how: '💪 कस्टमर कन्फर्म करे तो प्रोडक्ट ID से ऑर्डर करें।', btn: 'अभी शेयर करें', cancel: 'रद्द करें' },
  ta: { lang: 'தமிழ்', title: '🎉 அருமை! எங்களுடன் சம்பாதிக்க தயாரா?', note1: '✅ நாங்கள் எங்கள் வெப்சைட், வாட்ஸ்அப் எண், விலை அல்லது எந்த விவரங்களையும் பகிர மாட்டோம்.', note2: '✅ உங்கள் விலையை நீங்களே தீர்மானியுங்கள்.', whatTitle: 'என்ன பகிரப்படும்:', what1: '📝 பொருளின் பெயர் மற்றும் விளக்கம்', what2: '🖼 2 படங்கள்', what3: '🔑 Product ID', how: '💪 Product ID மூலம் ஆர்டர் செய்யுங்கள்!', btn: 'இப்போது பகிர்', cancel: 'ரத்து செய்' },
  te: { lang: 'తెలుగు', title: '🎉 అద్భుతం! మాతో సంపాదించడానికి సిద్ధంగా ఉన్నారా?', note1: '✅ మేము మా వెబ్సైట్, WhatsApp నంబర్, ధర లేదా ఏ వివరాలూ షేర్ చేయము.', note2: '✅ మీ ధరను మీరే నిర్ణయించుకోండి.', whatTitle: 'ఏమి షేర్ అవుతుంది:', what1: '📝 ప్రోడక్ట్ పేరు మరియు వివరణ', what2: '🖼 2 ఫోటోలు', what3: '🔑 Product ID', how: '💪 Product ID తో ఆర్డర్ చేయండి!', btn: 'ఇప్పుడు షేర్ చేయండి', cancel: 'రద్దు చేయి' },
  kn: { lang: 'ಕನ್ನಡ', title: '🎉 ಅದ್ಭುತ! ನಮ್ಮೊಂದಿಗೆ ಗಳಿಸಲು ತಯಾರಾಗಿದ್ದೀರಾ?', note1: '✅ ನಾವು ನಮ್ಮ ವೆಬ್‌ಸೈಟ್, WhatsApp ನಂಬರ್, ಬೆಲೆ ಹಂಚಿಕೊಳ್ಳುವುದಿಲ್ಲ.', note2: '✅ ನಿಮ್ಮ ಬೆಲೆಯನ್ನು ನೀವೇ ನಿರ್ಧರಿಸಿ.', whatTitle: 'ಏನು ಹಂಚಿಕೊಳ್ಳಲಾಗುತ್ತದೆ:', what1: '📝 ಉತ್ಪನ್ನದ ಹೆಸರು', what2: '🖼 2 ಚಿತ್ರಗಳು', what3: '🔑 Product ID', how: '💪 Product ID ಮೂಲಕ ಆರ್ಡರ್ ಮಾಡಿ!', btn: 'ಈಗ ಹಂಚಿಕೊಳ್ಳಿ', cancel: 'ರದ್ದುಮಾಡಿ' },
  ml: { lang: 'മലയാളം', title: '🎉 അടിപൊളി! ഞങ്ങളോടൊപ്പം സമ്പാദിക്കാൻ തയ്യാറാണോ?', note1: '✅ ഞങ്ങൾ വെബ്സൈറ്റ്, WhatsApp നമ്പർ, വില ഷെയർ ചെയ്യില്ല.', note2: '✅ നിങ്ങളുടെ വില നിങ്ങൾ തീരുമാനിക്കൂ.', whatTitle: 'എന്ത് ഷെയർ ചെയ്യും:', what1: '📝 ഉൽപ്പന്നത്തിന്റെ പേര്', what2: '🖼 2 ഫോട്ടോകൾ', what3: '🔑 Product ID', how: '💪 Product ID ഉപയോഗിച്ച് ഓർഡർ ചെയ്യൂ!', btn: 'ഇപ്പോൾ ഷെയർ ചെയ്യൂ', cancel: 'റദ്ദാക്കുക' },
  mr: { lang: 'मराठी', title: '🎉 जबरदस्त! आमच्यासोबत कमाई करायला तयार आहात?', note1: '✅ आम्ही वेबसाइट, WhatsApp नंबर, किंमत शेअर करणार नाही.', note2: '✅ तुमची किंमत तुम्हीच ठरवा.', whatTitle: 'काय शेअर होईल:', what1: '📝 उत्पादनाचे नाव', what2: '🖼 2 फोटो', what3: '🔑 Product ID', how: '💪 Product ID वापरून ऑर्डर करा!', btn: 'आत्ता शेअर करा', cancel: 'रद्द करा' },
  bn: { lang: 'বাংলা', title: '🎉 দারুণ! আমাদের সাথে আয় করতে প্রস্তুত?', note1: '✅ আমরা ওয়েবসাইট, WhatsApp নম্বর, দাম শেয়ার করব না।', note2: '✅ আপনার দাম আপনি ঠিক করুন।', whatTitle: 'কী শেয়ার হবে:', what1: '📝 পণ্যের নাম', what2: '🖼 2টি ছবি', what3: '🔑 Product ID', how: '💪 Product ID দিয়ে অর্ডার করুন!', btn: 'এখনই শেয়ার করুন', cancel: 'বাতিল করুন' },
  gu: { lang: 'ગુજરાતી', title: '🎉 જબરદસ્ત! અમારી સાથે કમાણી કરવા તૈયાર છો?', note1: '✅ અમે વેબસાઇટ, WhatsApp નંબર, ભાવ શેર નહીં કરીએ.', note2: '✅ તમારો ભાવ તમે નક્કી કરો.', whatTitle: 'શું શેર થશે:', what1: '📝 પ્રોડક્ટનું નામ', what2: '🖼 2 ફોટો', what3: '🔑 Product ID', how: '💪 Product ID થી ઓર્ડર કરો!', btn: 'હમણાં શેર કરો', cancel: 'રદ કરો' },
};
export default function ProductDetail() {
  const { id } = useParams();
  const { products, loading } = useProducts();
  const { addToCart, updateCartItemSelection, updateQty, removeFromCart, toggleWishlist, wishlist, cart } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const editState = location.state || {};
  const isEditMode = !!editState.editKey;
  const [size, setSize] = useState('');
  const [activeImg, setActiveImg] = useState(0);
  const [confirmedColorIndex, setConfirmedColorIndex] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareLang, setShareLang] = useState('en');
  const [cartError, setCartError] = useState('');
  const [selectedImageIndices, setSelectedImageIndices] = useState([]); // NEW (v13)
  const MAX_SHARE_IMAGES = 5;
  const touchStartXRef = useRef(null);
  const product = products.find(p => p.id === id);
  const isWishlisted = wishlist.includes(id);
  const isGarment = product?.category === 'Garments';
  const isShoe = product?.category === 'Shoes';
  const showSizeSelector = Boolean(product?.hasSizing && (isGarment || isShoe));
  const standardSizes = isShoe ? SHOE_SIZES : GARMENT_SIZES;
  const availableSizes = (product?.sizes && product.sizes.length > 0) ? product.sizes : standardSizes;
  const formattedDate = product ? formatDate(product.updateDate) : '';
  React.useEffect(() => {
    if (product) pixelViewContent(product);
    setActiveImg(0);
    const imgCount = product?.images?.length || 0;
    setConfirmedColorIndex(imgCount <= 1 ? 0 : null);
  }, [product]);
  // Size is never auto-picked in normal flow — starts blank on every
  // product change. EXCEPTION: edit mode pre-fills whatever was already
  // selected on the cart line being fixed, so the customer only has to
  // complete what's actually missing, not redo everything.
  useEffect(() => {
    if (isEditMode && editState.editSize) {
      setSize(editState.editSize);
    } else {
      setSize('');
    }
  }, [product?.id]);
  // Edit mode: pre-select the matching color thumbnail/preview if one
  // was already chosen on the cart line being fixed.
  useEffect(() => {
    if (!isEditMode || !editState.editColorCode || !product?.images?.length) return;
    const match = product.images.findIndex((_, i) => `CC${String(i + 1).padStart(2, '0')}` === editState.editColorCode);
    if (match !== -1) {
      setActiveImg(match);
      setConfirmedColorIndex(match);
    }
  }, [isEditMode, editState.editColorCode, product]);
  if (loading) return (
    <div className="app-shell">
      <Navbar showBack title="" />
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div className="prod-skeleton" style={{ height: 400, borderRadius: 0, marginBottom: 20 }}></div>
        <div className="prod-skeleton" style={{ height: 20, width: '60%', margin: '0 auto 12px' }}></div>
        <div className="prod-skeleton" style={{ height: 16, width: '40%', margin: '0 auto' }}></div>
      </div>
    </div>
  );
  if (!product) return (
    <div className="app-shell">
      <Navbar showBack title="Product" />
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
        <i className="ti ti-alert-circle" style={{ fontSize: 48, marginBottom: 16, display: 'block' }}></i>
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>Product not found</div>
        <button className="btn-blue" style={{ width: 'auto', padding: '12px 28px', marginTop: 16 }} onClick={() => navigate('/shop')}>Back to shop</button>
      </div>
    </div>
  );
  const images = product.images?.length > 0 ? product.images : (product.imageUrl ? [product.imageUrl] : []);
  const isFreeShip = product.shipping?.toUpperCase().includes('FREE');
  const t = SHARE_TRANSLATIONS[shareLang];
  const colorCodeFor = (i) => `CC${String(i + 1).padStart(2, '0')}`;
  const needsColorChoice = images.length > 1;
  const previewColorCode = images.length > 0 ? colorCodeFor(activeImg) : null;
  const currentSizeVal = showSizeSelector ? size : null;
  function colorIsInCart(cc) {
    return cart.some(i => i.id === product.id && i.colorCode === cc);
  }
  const exactCartKey = `${id}__${currentSizeVal || 'NA'}__${previewColorCode || 'NA'}`;
  const cartItem = isEditMode ? null : cart.find(i => i.key === exactCartKey);
  const cartQty = cartItem?.qty || 0;
  const previewAlreadyInCart = isEditMode ? false : !!cartItem;
  const previewIsConfirmed = confirmedColorIndex === activeImg;
  const canAddToCart = (!needsColorChoice || previewIsConfirmed || previewAlreadyInCart) && (!showSizeSelector || availableSizes.includes(size));
  function previewImage(i) {
    setActiveImg(i);
  }
  function confirmColor() {
    setConfirmedColorIndex(activeImg);
  }
  function handleAddToCart() {
    if (!canAddToCart) return;
    setCartError('');
    const colorImageUrl = images[activeImg] || product.imageUrl || '';
    if (isEditMode) {
      const result = updateCartItemSelection(
        editState.editKey,
        product,
        { size: currentSizeVal, colorCode: previewColorCode, colorImageUrl },
        editState.editQty || 1
      );
      if (result?.error) {
        setCartError(result.error);
        return;
      }
      pixelAddToCart(product, editState.editQty || 1);
      navigate('/cart');
      return;
    }
    const result = addToCart(product, 1, {
      size: currentSizeVal,
      colorCode: previewColorCode,
      colorImageUrl,
    });
    if (result?.error) {
      setCartError(result.error);
      return;
    }
    pixelAddToCart(product, 1);
  }
  function handleIncrement() { updateQty(cartItem.key, cartQty + 1); }
  function handleDecrement() {
    if (cartQty <= 1) removeFromCart(cartItem.key);
    else updateQty(cartItem.key, cartQty - 1);
  }
  function handleWhatsApp() {
    const msg = `Hi! I'm interested in this product:\n\nProduct ID: ${product.id}\nProduct: ${product.name}\nPrice: ₹${product.price.toLocaleString()}\n\nPlease share availability and shipping details.`;
    window.open(`https://wa.me/919363012496?text=${encodeURIComponent(msg)}`, '_blank');
  }
  function toggleShareImage(idx) {
    setSelectedImageIndices(prev => {
      if (prev.includes(idx)) return prev.filter(i => i !== idx);
      if (prev.length >= MAX_SHARE_IMAGES) return prev; // cap at 5, ignore further taps
      return [...prev, idx];
    });
  }
  async function handleShare() {
    const shareText = `${product.name}\n\n${product.description?.substring(0, 200) || ''}\n\nProduct ID: ${product.id}`;
    try {
      // CHANGED (v13): use the reseller's actual selection instead of
      // always the first 5, preserving image order (not tap order).
      const imageUrls = selectedImageIndices.length > 0
        ? images.filter((_, i) => selectedImageIndices.includes(i))
        : images.slice(0, MAX_SHARE_IMAGES);
      const imageFiles = [];
      for (const url of imageUrls) {
        try {
          // FIXED (19 Jul 2026): fetch product images through OUR OWN
          // domain (/.netlify/functions/image-proxy) instead of
          // fetching the Google Drive URL directly from the browser.
          // Drive doesn't send CORS headers, so a direct browser
          // fetch() ALWAYS failed silently here before — Share & Earn
          // was falling back to text-only every single time, on every
          // device, regardless of browser. The proxy fetches
          // server-side (no CORS restriction applies there) and
          // streams the bytes back from our own domain, which the
          // browser is always allowed to fetch from.
          const proxiedUrl = `/.netlify/functions/image-proxy?url=${encodeURIComponent(url)}`;
          const response = await fetch(proxiedUrl);
          if (!response.ok) throw new Error(`Image proxy returned ${response.status}`);
          const blob = await response.blob();
          // Use the REAL content-type returned by the proxy instead of
          // hardcoding image/jpeg — a mismatched declared type (e.g. an
          // actual PNG labeled as JPEG) can cause navigator.canShare()
          // to silently reject the file on some devices.
          const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
          const file = new File([blob], `product-${product.id}.${ext}`, { type: blob.type || 'image/jpeg' });
          imageFiles.push(file);
        } catch (e) {
          // NOT silently swallowed anymore — logged so a real failure
          // (e.g. proxy misconfigured, wrong image host) is visible in
          // the browser console instead of just quietly degrading to
          // text-only every time with no trace of why.
          console.error('Share & Earn: could not attach image', url, e);
        }
      }
      if (navigator.share) {
        if (imageFiles.length > 0 && navigator.canShare && navigator.canShare({ files: imageFiles })) {
          await navigator.share({ title: product.name, text: shareText, files: imageFiles });
        } else {
          await navigator.share({ title: product.name, text: shareText });
        }
      } else {
        navigator.clipboard.writeText(shareText);
        alert('Product details copied!');
      }
    } catch (e) {
      console.error('Share & Earn: share failed', e);
    }
    setShowSharePopup(false);
  }
  function handleCopyId() {
    navigator.clipboard.writeText(product.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  function prevImg() { previewImage((activeImg - 1 + images.length) % images.length); }
  function nextImg() { previewImage((activeImg + 1) % images.length); }
  function handleTouchStart(e) {
    touchStartXRef.current = e.touches[0].clientX;
  }
  function handleTouchEnd(e) {
    if (touchStartXRef.current === null || images.length <= 1) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const SWIPE_THRESHOLD = 40;
    if (deltaX > SWIPE_THRESHOLD) {
      prevImg();
    } else if (deltaX < -SWIPE_THRESHOLD) {
      nextImg();
    }
    touchStartXRef.current = null;
  }
  return (
    <div className="app-shell">
      <Navbar showBack title="" />
      {isEditMode && (
        <div style={{ background: '#FEF8E8', color: '#854F0B', fontSize: 12, padding: '10px 16px', textAlign: 'center', fontWeight: 500 }}>
          <i className="ti ti-edit" style={{ marginRight: 6 }}></i>
          Completing your cart item — select the missing option below
        </div>
      )}
      {cartError && (
        <div style={{ background: '#FDECEC', color: '#D64545', fontSize: 12, padding: '10px 16px', textAlign: 'center', fontWeight: 500 }}>
          <i className="ti ti-alert-circle" style={{ marginRight: 6 }}></i>
          {cartError}
        </div>
      )}
      {/* Share popup */}
      {showSharePopup && (
        <div className="share-popup-overlay" onClick={() => setShowSharePopup(false)}>
          <div className="share-popup" onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 16px 10px', borderBottom: '0.5px solid var(--border)' }}>
              <select value={shareLang} onChange={e => setShareLang(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--navy)', background: '#fff', fontFamily: 'var(--font)', outline: 'none' }}>
                {Object.keys(SHARE_TRANSLATIONS).map(lang => (
                  <option key={lang} value={lang}>{SHARE_TRANSLATIONS[lang].lang}</option>
                ))}
              </select>
            </div>
            {images.length > 0 && (
              <div className="share-img-picker">
                <div className="share-img-picker-label">
                  Choose up to {MAX_SHARE_IMAGES} photos to share ({selectedImageIndices.length}/{MAX_SHARE_IMAGES} selected)
                </div>
                <div className="share-img-picker-row">
                  {images.map((img, i) => {
                    const isSelected = selectedImageIndices.includes(i);
                    return (
                      <button
                        key={i}
                        className={`share-img-thumb ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleShareImage(i)}
                        type="button"
                      >
                        <img src={img} alt={`Option ${i + 1}`} />
                        {isSelected && <span className="share-img-check"><i className="ti ti-check"></i></span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="share-popup-content">
              <div className="share-popup-title">{t.title}</div>
              <p className="share-popup-text">{t.note1}</p>
              <p className="share-popup-text">{t.note2}</p>
              <div className="share-popup-section">{t.whatTitle}</div>
              <p className="share-popup-text">{t.what1}</p>
              <p className="share-popup-text">{t.what2}</p>
              <p className="share-popup-text">{t.what3}</p>
              <p className="share-popup-text" style={{ marginTop: 12, fontStyle: 'italic' }}>{t.how}</p>
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '0 16px 16px' }}>
              <button className="share-popup-cancel" onClick={() => setShowSharePopup(false)}>{t.cancel}</button>
              <button className="share-popup-btn" onClick={handleShare}>{t.btn}</button>
            </div>
          </div>
        </div>
      )}
      {/* Image gallery */}
      <div
        className={`pd-img-area ${isGarment ? 'pd-img-g' : 'pd-img-j'}`}
        style={{ touchAction: 'pan-y' }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {images.length > 0 ? (
          <img src={images[activeImg]} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain', touchAction: 'pinch-zoom' }} />
        ) : (
          <i className={`ti ${isGarment ? 'ti-shirt' : 'ti-diamond'}`} style={{ fontSize: 90, opacity: 0.2, color: 'var(--navy)' }} />
        )}
        {images.length > 1 && (
          <div className="pd-count-badge">{activeImg + 1}/{images.length}</div>
        )}
        {images.length > 1 && (
          <>
            <button className="pd-arrow pd-arrow-left" onClick={prevImg}>&#8249;</button>
            <button className="pd-arrow pd-arrow-right" onClick={nextImg}>&#8250;</button>
          </>
        )}
        <button className={`pd-wish ${isWishlisted ? 'liked' : ''}`} onClick={() => toggleWishlist(id)}>
          <i className={`ti ${isWishlisted ? 'ti-heart-filled' : 'ti-heart'}`}></i>
        </button>
        {images.length > 1 && (
          <div className="pd-dots">
            {images.map((_, i) => (
              <button key={i} className={`pd-dot ${i === activeImg ? 'active' : ''}`} onClick={() => previewImage(i)} />
            ))}
          </div>
        )}
      </div>
      {/* Dedicated color-confirm bar — the ONLY thing that actually selects a color */}
      {needsColorChoice && (
        previewAlreadyInCart ? (
          <div className="pd-color-bar in-cart">
            <i className="ti ti-check"></i> In your cart · {previewColorCode}
          </div>
        ) : previewIsConfirmed ? (
          <button className="pd-color-bar confirmed" onClick={() => setConfirmedColorIndex(null)}>
            <i className="ti ti-check"></i> Selected · {previewColorCode}
            <i className="ti ti-x" style={{ marginLeft: 8, fontSize: 14 }}></i>
          </button>
        ) : (
          <button className="pd-color-bar action" onClick={confirmColor}>
            Select this color · {previewColorCode}
          </button>
        )
      )}
      {images.length > 1 && (
        <div className="pd-thumbs">
          {images.map((img, i) => {
            const cc = colorCodeFor(i);
            const inCart = isEditMode ? false : colorIsInCart(cc);
            const isConfirmedOnly = !inCart && confirmedColorIndex === i;
            return (
              <button key={i} className={`pd-thumb ${i === activeImg ? 'active' : ''}`} onClick={() => previewImage(i)}>
                <img src={img} alt={`View ${i + 1}`} />
                {inCart ? (
                  <span className="pd-thumb-check in-cart"><i className="ti ti-check"></i></span>
                ) : isConfirmedOnly ? (
                  <span className="pd-thumb-check confirmed"><i className="ti ti-check"></i></span>
                ) : null}
                <span className="pd-thumb-cc">{cc}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="pd-body">
        <div className="pd-id-row">
          <span className="pd-id-label">Product ID:</span>
          <span className="pd-id-value">{product.id}</span>
          <button className="pd-id-copy" onClick={handleCopyId}>
            <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`}></i>
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
          {formattedDate && (
            <span className="pd-id-date">&nbsp;|&nbsp;{formattedDate}</span>
          )}
        </div>
        <div style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--blue)', textTransform: 'uppercase', marginBottom: 6 }}>{product.category}</div>
        <h1 style={{ fontSize: 18, fontWeight: 500, marginBottom: 14, lineHeight: 1.4 }}>{product.name}</h1>
        <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 8 }}>₹{product.price.toLocaleString()}</div>
        <div className={`pd-ship-info ${isFreeShip ? 'free' : 'extra'}`}>
          <i className={`ti ${isFreeShip ? 'ti-truck' : 'ti-info-circle'}`}></i>
          <span>{isFreeShip ? 'Free shipping included on this item.' : 'Shipping charges apply. Exact amount based on item weight & your pincode — confirmed via WhatsApp after order. Products dispatched only after full payment including shipping.'}</span>
        </div>
        {showSizeSelector && (
          <>
            <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10, marginTop: 20 }}>Select size</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {availableSizes.map(s => (
                <button
                  key={s}
                  className={`size-btn ${size === s ? 'active' : ''}`}
                  onClick={() => setSize(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </>
        )}
        <p style={{ fontSize: 13, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>{product.description}</p>
        <div className="pd-perks">
          {[['ti-map-pin', 'Pan-India Delivery'], ['ti-shield-check', 'Quality Guaranteed'], ['ti-qrcode', 'Secure UPI Payment'], ['ti-brand-whatsapp', 'WhatsApp Support']].map(([icon, text]) => (
            <div key={icon} className="pd-perk">
              <i className={`ti ${icon}`}></i>
              <span>{text}</span>
            </div>
          ))}
        </div>
        {(needsColorChoice || showSizeSelector) && (
          <div className="pd-autofill-note">
            <i className="ti ti-info-circle"></i>
            <div>
              Your selected {needsColorChoice && showSizeSelector ? 'color and size are' : needsColorChoice ? 'color is' : 'size is'} saved with your order automatically — no need to message us.
            </div>
          </div>
        )}
      </div>
      {/* Three button CTA row */}
      <div className="pd-cta">
        {isEditMode ? (
          <button className="pd-btn-cart" style={{ flex: 2.4, ...(!canAddToCart ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }} onClick={handleAddToCart} disabled={!canAddToCart}>
            Update cart item
          </button>
        ) : (
          <>
            {cartQty > 0 ? (
              <div className="pd-counter">
                <button className="pd-counter-btn" onClick={handleDecrement}>−</button>
                <span className="pd-counter-val">{cartQty}</span>
                <button className="pd-counter-btn" onClick={handleIncrement}>+</button>
              </div>
            ) : (
              <button className="pd-btn-cart" onClick={handleAddToCart} disabled={!canAddToCart} style={!canAddToCart ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
                Add to cart
              </button>
            )}
            {cartQty > 0 ? (
              <button className="pd-btn-gocart" onClick={() => navigate('/cart')}>
                Go to cart →
              </button>
            ) : (
              <button className="pd-btn-wa" onClick={handleWhatsApp}>
                <i className="ti ti-brand-whatsapp"></i>
                Enquire
              </button>
            )}
          </>
        )}
        {/* Share & Earn */}
        <button
          className="pd-btn-share"
          onClick={() => {
            // NEW (v13): default to the first 5 images pre-selected,
            // same behavior as before if the reseller doesn't touch it.
            setSelectedImageIndices(images.slice(0, MAX_SHARE_IMAGES).map((_, i) => i));
            setShowSharePopup(true);
          }}
        >
          <i className="ti ti-share"></i>
          Share & Earn
        </button>
      </div>
      <style>{`
        .pd-img-area { height: 380px; display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; touch-action: pinch-zoom; }
        .pd-img-g { background: #EEF2F8; }
        .pd-img-j { background: #F7F0E2; }
        .pd-arrow { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.85); border: none; color: var(--navy); font-size: 24px; width: 36px; height: 36px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 3; padding: 0; box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
        .pd-arrow-left { left: 10px; }
        .pd-arrow-right { right: 10px; }
        .pd-wish { position: absolute; top: 14px; right: 14px; width: 36px; height: 36px; background: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: none; cursor: pointer; z-index: 4; }
        .pd-wish i { font-size: 18px; color: var(--muted); }
        .pd-wish.liked i { color: #E24B4A; }
        .pd-count-badge { position: absolute; top: 14px; left: 14px; z-index: 4; background: rgba(0,0,0,0.55); color: #fff; font-size: 11px; font-weight: 500; padding: 4px 9px; border-radius: 12px; }
        .pd-dots { position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); display: flex; gap: 5px; z-index: 2; }
        .pd-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(27,42,74,0.2); border: none; cursor: pointer; padding: 0; transition: all 0.2s; }
        .pd-dot.active { background: var(--navy); width: 16px; border-radius: 3px; }
        .pd-color-bar { display: block; width: 100%; text-align: center; padding: 13px 12px; font-size: 15px; font-weight: 700; border: none; cursor: pointer; font-family: var(--font); letter-spacing: 0.01em; }
        .pd-color-bar.action { background: var(--navy); color: #fff; }
        .pd-color-bar.confirmed { background: var(--blue); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .pd-color-bar.in-cart { background: var(--green); color: #fff; }
        .pd-color-bar i { margin-right: 6px; }
        .pd-thumbs { display: flex; gap: 8px; padding: 10px 14px; overflow-x: auto; background: #fff; border-bottom: 0.5px solid var(--border); scrollbar-width: none; }
        .pd-thumbs::-webkit-scrollbar { display: none; }
        .pd-thumb { position: relative; width: 56px; height: 56px; border-radius: 6px; overflow: hidden; border: 2px solid transparent; cursor: pointer; background: none; padding: 0; flex-shrink: 0; }
        .pd-thumb.active { border-color: var(--navy); }
        .pd-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .pd-thumb-check { position: absolute; top: 2px; right: 2px; width: 15px; height: 15px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 9px; }
        .pd-thumb-check.in-cart { background: var(--green); }
        .pd-thumb-check.confirmed { background: var(--blue); }
        .pd-thumb-cc { position: absolute; bottom: 0; left: 0; right: 0; background: rgba(0,0,0,0.55); color: #fff; font-size: 8px; text-align: center; padding: 2px 0; }
        .pd-body { padding: 16px 16px 100px; }
        .pd-id-row { display: flex; align-items: center; gap: 8px; background: #F4F2EE; border-radius: 6px; padding: 10px 12px; margin-bottom: 14px; max-width: fit-content; }
        .pd-id-label { font-size: 10px; color: var(--muted); letter-spacing: 0.06em; text-transform: uppercase; }
        .pd-id-value { font-size: 12px; font-weight: 600; color: var(--navy); letter-spacing: 0.04em; white-space: nowrap; }
        .pd-id-date { font-size: 11px; color: var(--muted); white-space: nowrap; }
        .pd-id-copy { display: flex; align-items: center; gap: 4px; background: var(--navy); color: #fff; border: none; padding: 5px 10px; border-radius: 4px; font-size: 10px; cursor: pointer; font-family: var(--font); }
        .pd-ship-info { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border-radius: 8px; margin-bottom: 16px; font-size: 12px; line-height: 1.6; }
        .pd-ship-info.free { background: var(--green); color: #fff; }
        .pd-ship-info.extra { background: #E24B4A; color: #fff; }
        .pd-ship-info i { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
        .pd-autofill-note { display: flex; gap: 10px; background: #EEF4FF; border-radius: 8px; padding: 12px 14px; margin-bottom: 20px; font-size: 12px; color: var(--navy); line-height: 1.6; }
        .pd-autofill-note i { font-size: 16px; flex-shrink: 0; color: var(--blue); margin-top: 1px; }
        .size-btn { width: 42px; height: 42px; border-radius: 6px; border: 0.5px solid rgba(27,42,74,0.2); background: #fff; font-size: 12px; color: var(--navy); cursor: pointer; font-family: var(--font); }
        .size-btn.active { background: var(--navy); color: var(--off-white); border-color: var(--navy); }
        .size-btn.unavailable { text-decoration: line-through; opacity: 0.35; cursor: not-allowed; background: #F4F2EE; }
        .pd-perks { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
        .pd-perk { display: flex; align-items: center; gap: 8px; background: #fff; border-radius: 8px; padding: 10px 12px; border: 0.5px solid var(--card-border); font-size: 11px; color: #555; line-height: 1.4; }
        .pd-perk i { font-size: 18px; color: var(--blue); flex-shrink: 0; }
        /* Three button CTA */
        .pd-cta { position: fixed; bottom: 0; left: 50%; transform: translateX(-50%); width: 100%; max-width: 430px; background: #fff; border-top: 0.5px solid var(--border); padding: 10px 12px 20px; display: flex; gap: 8px; z-index: 50; align-items: center; }
        .pd-btn-cart { flex: 1.2; background: var(--navy); color: #fff; border: none; padding: 12px 8px; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: var(--font); }
        .pd-btn-wa { flex: 1.2; background: #25D366; color: #fff; border: none; padding: 12px 8px; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: var(--font); display: flex; align-items: center; justify-content: center; gap: 4px; }
        .pd-btn-gocart { flex: 1.2; background: var(--green); color: #fff; border: none; padding: 12px 8px; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; font-family: var(--font); }
        .pd-btn-share { flex: 1.2; background: var(--blue); color: #fff; border: none; padding: 12px 8px; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: var(--font); display: flex; align-items: center; justify-content: center; gap: 4px; animation: shareEarnPulse 1.5s ease-in-out infinite; }
        .pd-btn-share i { font-size: 14px; }
        @keyframes shareEarnPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(47,111,237,0.4); } 50% { box-shadow: 0 0 0 6px rgba(47,111,237,0); } }
        /* Counter */
        .pd-counter { flex: 1.2; display: flex; align-items: center; border: 1.5px solid var(--navy); border-radius: 6px; overflow: hidden; }
        .pd-counter-btn { width: 34px; height: 42px; border: none; background: var(--navy); color: #fff; font-size: 18px; cursor: pointer; font-family: var(--font); display: flex; align-items: center; justify-content: center; }
        .pd-counter-val { flex: 1; text-align: center; font-size: 14px; font-weight: 600; color: var(--navy); }
        /* Popup */
        .share-popup-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-end; justify-content: center; }
        .share-popup { background: #fff; border-radius: 16px 16px 0 0; width: 100%; max-width: 430px; max-height: 85vh; overflow-y: auto; }
        .share-popup-content { padding: 16px; }
        .share-popup-title { font-size: 16px; font-weight: 700; color: var(--navy); margin-bottom: 14px; line-height: 1.4; }
        .share-popup-text { font-size: 13px; color: #444; line-height: 1.7; margin-bottom: 8px; }
        .share-popup-section { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--blue); margin: 14px 0 8px; }
        .share-popup-btn { flex: 2; background: var(--blue); color: #fff; border: none; padding: 14px; border-radius: 6px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: var(--font); }
        .share-popup-cancel { flex: 1; background: transparent; color: var(--muted); border: 0.5px solid var(--border); padding: 14px; border-radius: 6px; font-size: 13px; cursor: pointer; font-family: var(--font); }
        /* NEW (v13): image picker for Share & Earn */
        .share-img-picker { padding: 14px 16px 0; }
        .share-img-picker-label { font-size: 11px; color: var(--muted); margin-bottom: 8px; }
        .share-img-picker-row { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px; scrollbar-width: none; }
        .share-img-picker-row::-webkit-scrollbar { display: none; }
        .share-img-thumb { position: relative; flex-shrink: 0; width: 60px; height: 60px; border-radius: 6px; overflow: hidden; border: 2px solid transparent; padding: 0; cursor: pointer; background: none; opacity: 0.5; }
        .share-img-thumb.selected { border-color: var(--blue); opacity: 1; }
        .share-img-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .share-img-check { position: absolute; top: 3px; right: 3px; width: 16px; height: 16px; border-radius: 50%; background: var(--blue); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 10px; }
      `}</style>
    </div>
  );
}